import styled from "@emotion/styled";
import colors from "../../styles/colors";
import ReactSelect from "react-select";
function getFieldBorder(showError, disabled) {
    if (disabled) {
        return "0px";
    }
    else if (showError) {
        return `1px solid ${colors["color-red-60"]}`;
    }
    return `1px solid ${colors["color-gray-80"]}`;
}
/**
 * Styled compound field container.
 */
export const CompoundFieldContainer = styled.div `
  display: flex;
  flex-direction: row;
  border-radius: 0.375rem;
  border: ${(props) => getFieldBorder(props.showError, props.disabled)};
  font-family: Inter;
  background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-white-100"]};

  :first-child {
    background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-white-100"]};
  }

  input {
    background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-white-100"]};
  }

  &:focus-within {
    border: 1px solid
      ${(props) => props.showError ? colors["color-red-80"] : colors["color-blue-100"]};
    box-shadow: 0px 2px 4px 0px rgba(0, 112, 210, 0.15);
    background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-blue-30"]};

    :first-child {
      background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-blue-30"]};
    }
    input {
      background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-blue-30"]};
    }
  }
`;
/**
 * Styled input field container.
 */
export const InputFieldContainer = styled.div `
  padding: 4px 4px 4px 12px;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px 0 0 6px;
  gap: 0.188rem;
  background-color: ${(props) => props.disabled ? colors["color-gray-30"] : ""};
`;
/**
 * Styled label div.
 */
export const Label = styled.label `
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 0;
  width: 100%;
  border-radius: 6px;
  font-size: 0.5625rem;
  font-weight: 500;
  line-height: 0.84375rem;
  color: ${colors["color-gray-140"]};
`;
/**
 * Styled input field prefix container.
 */
export const InputFieldPrefixSuffixContainer = styled.div `
  display: flex;
  align-items: center;
  position: relative;
`;
/**
 * Styled suffix div.
 */
export const StyledSuffix = styled.div `
  left: ${(props) => (props.fullWidth ? "calc(100% - 1.25rem)" : "18.25rem")};
  position: absolute;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  color: ${colors["color-blue-180"]};
`;
/**
 * Styled prefix div.
 */
export const StyledPrefix = styled.div `
  position: absolute;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  color: ${colors["color-blue-180"]};
`;
/**
 * Styled Input field component.
 */
export const InputField = styled.input `
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.125rem;
  color: ${colors["color-blue-180"]};
  border: none;
  width: 100%;
  padding-left: ${(props) => (props.hasPrefix ? ".5rem" : "0")};
  padding-right: ${(props) => (props.hasSuffix ? "5rem" : "0")};
  ::placeholder {
    color: ${(props) => getPlaceholderTextColor(props.showError)};
  }

  :hover {
    ::placeholder {
      color: ${(props) => getPlaceholderTextColor(props.showError)};
    }
  }

  :focus {
    outline-width: 0;
    background-color: ${colors["color-blue-30"]};

    ::placeholder {
      color: ${(props) => getPlaceholderTextColor(props.showError)};
    }
  }

  :disabled {
    border: 0.078rem solid ${colors["color-gray-30"]};
    background-color: ${colors["color-gray-30"]};
    color: ${colors["color-gray-120"]};

    :hover {
      box-shadow: none;
      ::placeholder {
        color: ${colors["color-gray-120"]};
      }
    }
  }
`;
/**
 * Function to return css for placeholder text for input field component
 * @param {boolean} showError
 * @returns {string} updated css
 */
export function getPlaceholderTextColor(showError) {
    if (showError)
        return colors["color-red-160"];
    else
        return colors["color-gray-100"];
}
function getBackground(showError, isDisabled) {
    if (isDisabled) {
        return colors["color-gray-40"];
    }
    else if (showError) {
        return colors["color-red-40"];
    }
    return colors["color-blue-40"];
}
/**
 * Styled Element to render React-Select component
 */
export const ReactSelectStyled = styled(ReactSelect) `
  height: 100%;
  width: max-content;
  .react-select__placeholder,
  .react-select__single-value {
    font-family: Inter;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.313rem;
    letter-spacing: 0em;
    text-align: left;
    color: ${colors["color-blue-180"]} !important;
    margin: 0;
    padding-left: 2px;
  }

  .react-select__single-value {
    color: ${colors["color-black-100"]};
  }

  .react-select__control {
    height: 100%;
    padding-right: 5px;
    min-height: initial;
    background: ${(props) => getBackground(props.showError, props.isDisabled)};
    box-shadow: none;
    border: none;
    border-radius: 0px 0.275rem 0.275rem 0px;
    outline-width; 0;
    cursor: pointer;
    :hover {
      box-shadow: 0rem 0.125rem 0.25rem 0rem rgba(0, 0, 0, 0.1),
      0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.28);
    }

    :focus-within {
      outline: none !important;
      border: none;
      box-shadow: none;
      background: ${(props) => props.showError ? colors["color-red-40"] : colors["color-blue-40"]};
    }
  }

  .react-select__control--is-focused {
    border-color: initial;
  }

  .react-select__menu-list {
    padding: 0px;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.20);
    border-radius: 0px 0px 6px 6px;
    outline: none;
  }

  .react-select__menu {
    margin: 0.125rem 0rem 0rem 0rem;
  }

  .react-select__option, .react-select__menu-notice {
    font-family: Inter;
    font-size: .875rem;
    font-weight: 400;
    line-height: 1.313rem;
    letter-spacing: 0em;
    text-align: left;
    display: flex;
    align-items: center;
    background: ${colors["color-white-100"]};
    cursor: pointer;

    :hover{
      background: ${(props) => props.showError ? colors["color-red-20"] : colors["color-blue-30"]};
    }
  }

  .react-select__input {
    box-shadow: none;
  }

  .react-select__indicators path {
    stroke: ${(props) => props.isDisabled ? colors["color-gray-140"] : colors["color-blue-120"]};
  }
`;
/**
 * Styled div to render the select component
 */
export const ReactSelectContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: end;
`;
//# sourceMappingURL=CompoundUnitInputField.styled.js.map